import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './HomeSettings.module.scss';
import Loader from '../../Molecules/Loader/Loader';
import Toggle from '../../Atoms/Inputs/Toggle';
import { useSiteFeatures, useSaveSiteFeatures } from '../../../Hooks/SiteFeatures';
import {
  FEATURE_GROUPS,
  mergeFeatures,
  changedFeatures,
} from '../../../config/siteFeatures';

/**
 * «إعدادات عامة» — show and hide parts of the public site.
 *
 * Second section of `HomeSettingsPage`, which was built to grow: its own
 * comment says the tab row draws itself "ONLY once there is a second section
 * to choose between". This is that second section, so the tabs appear for the
 * first time with it.
 *
 * ---------------------------------------------------------------------------
 * NOTHING IS SAVED UNTIL «حفظ التغييرات» IS PRESSED, and that is a decision.
 *
 * An instant-apply switch is the nicer interaction and the wrong one here:
 * every switch on this screen changes what a LIVE PUBLIC SITE shows to every
 * visitor, and a mis-click would hide the media centre before the hand left
 * the mouse. So toggling edits a draft, the pending changes are listed back in
 * words before they are committed, and the operator presses save having read
 * what they are about to do.
 *
 * ---------------------------------------------------------------------------
 * THE ENDPOINT IS NOT DEPLOYED YET. That is a STATE, not an error: the screen
 * explains what it will control and keeps the switches readable at their
 * defaults, so an operator can see what is coming. Saving is disabled, because
 * a save that cannot persist is worse than a button that says why it is off.
 */
const GeneralSettings = ({ title, hint }) => {
  const { data, isLoading, isError, error, refetch } = useSiteFeatures();
  const save = useSaveSiteFeatures();

  /** key -> boolean, only for switches the operator has touched. */
  const [draft, setDraft] = useState({});
  const [failures, setFailures] = useState([]);

  const rows = useMemo(() => mergeFeatures(data?.items), [data]);
  const pending = useMemo(() => changedFeatures(rows, draft), [rows, draft]);

  /* A 404 means the route is not there yet. It is SILENT on purpose: the
     screen then shows the switches at the state the site is actually in, and
     an operator is not asked to read a paragraph about a deployment. Anything
     else — 401, 500, a dead network — is a real failure and says so. */
  const brokenOtherwise = isError && error?.status !== 404;

  const valueOf = (row) =>
    draft[row.key] === undefined ? row.isEnabled : draft[row.key];

  const toggle = (row) =>
    setDraft((prev) => ({ ...prev, [row.key]: !valueOf(row) }));

  const reset = () => {
    setDraft({});
    setFailures([]);
  };

  const commit = async () => {
    if (!pending.length) return;
    setFailures([]);

    try {
      const answer = await save.mutateAsync(
        pending.map(({ key, isEnabled }) => ({ key, isEnabled }))
      );

      /* THE BATCH MAY HALF-APPLY. The contract requires every item to be
         processed and a partial failure reported rather than hidden behind a
         success, so the per-item results are read before anything celebrates.
         A backend that answers without them is treated as full success — but
         the shape is handled the day it arrives. */
      const rejected = (answer?.items ?? []).filter(
        (item) => item && item.success === false
      );

      if (rejected.length) {
        setFailures(rejected);
        toast.error(`تعذّر تطبيق ${rejected.length} من ${pending.length} تغيير.`);
        // Keep the draft: the operator has to see which ones to retry.
        refetch();
        return;
      }

      toast.success(`تم حفظ ${pending.length} تغيير.`);
      reset();
    } catch (e) {
      toast.error(e.message);
    }
  };

  /* A group with no switches in it draws nothing — which is what keeps
     `homeSections` and `structure` out of the way while their rows are
     commented out of the catalogue. */
  const groups = useMemo(
    () =>
      FEATURE_GROUPS.map((group) => ({
        ...group,
        rows: rows.filter((row) => row.group === group.key),
      })).filter((group) => group.rows.length),
    [rows]
  );

  if (isLoading) return <Loader />;

  return (
    <div className={styles['menu-settings']}>
      {/* Same header classes as `MenuSettings`, not a second set: the two are
          tabs of one screen, and a heading that sits two pixels differently
          between them is the kind of thing that gets reported as a bug. */}
      <header className={styles['page-head']}>
        <div>
          <h1 className={styles['page-title']}>{title ?? 'إعدادات عامة'}</h1>
          {hint ? <p className={styles['page-subtitle']}>{hint}</p> : null}
        </div>
      </header>

      {brokenOtherwise ? (
        <div className={styles['general-error']}>
          <p>تعذّر قراءة الإعدادات: {error?.message}</p>
          <button type="button" onClick={() => refetch()}>
            إعادة المحاولة
          </button>
        </div>
      ) : null}

      {failures.length ? (
        <div className={styles['general-error']}>
          <p>لم تُطبَّق هذه المفاتيح:</p>
          <ul>
            {failures.map((item) => (
              <li key={item.key}>
                <code>{item.key}</code> — {item.error || item.message || 'سبب غير معروف'}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {groups.map((group) => (
        <div key={group.key} className={styles['general-group']}>
          <h3>{group.label}</h3>
          {group.hint ? <p className={styles['general-grouphint']}>{group.hint}</p> : null}

          <ul className={styles['general-list']}>
            {group.rows.map((row) => {
              const on = valueOf(row);
              const dirty = draft[row.key] !== undefined && draft[row.key] !== row.isEnabled;
              return (
                <li
                  key={row.key}
                  className={`${styles['general-row']} ${dirty ? styles['general-row-dirty'] : ''}`}
                >
                  <Toggle
                    isActive={on}
                    onChange={() => toggle(row)}
                    text=""
                    color="blue"
                    ariaLabel={`${row.label} — ${on ? 'ظاهر' : 'مخفي'}`}
                  />
                  <div className={styles['general-text']}>
                    <span className={styles['general-label']}>
                      {row.label}
                      {!row.isConfigured ? (
                        <em className={styles['general-tag']}>افتراضي</em>
                      ) : null}
                    </span>
                    {row.description ? <p>{row.description}</p> : null}
                    <span className={styles['general-state']}>
                      {on ? 'ظاهر للزوار' : 'مخفي عن الزوار'}
                      {dirty ? ' — لم يُحفظ بعد' : ''}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {/* THE CHANGES ARE READ BACK IN WORDS BEFORE THEY ARE COMMITTED. A count
          alone ("3 تغييرات") does not let anyone catch the switch they hit by
          accident, which is the whole reason this screen does not auto-save. */}
      <footer className={styles['general-actions']}>
        {pending.length ? (
          <div className={styles['general-pending']}>
            <p>سيتم تطبيق التغييرات التالية على الموقع المباشر:</p>
            <ul>
              {pending.map((row) => (
                <li key={row.key}>
                  <strong>{row.label}</strong> — {row.isEnabled ? 'إظهار' : 'إخفاء'}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className={styles['general-clean']}>لا توجد تغييرات غير محفوظة.</p>
        )}

        <div className={styles['general-buttons']}>
          <button
            type="button"
            className={styles['general-save']}
            disabled={!pending.length || save.isLoading}
            onClick={commit}
          >
            {save.isLoading ? 'جارٍ الحفظ…' : 'حفظ التغييرات'}
          </button>
          <button
            type="button"
            className={styles['general-cancel']}
            disabled={!pending.length || save.isLoading}
            onClick={reset}
          >
            تراجع
          </button>
        </div>
      </footer>
    </div>
  );
};

export default GeneralSettings;
